// src/components/Login.jsx - Versione con controllo account disabilitato
import { useState, useEffect } from "react";
import {
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signOut
} from "firebase/auth";
import { auth, db } from "../firebase";
import { doc, setDoc, getDoc, updateDoc } from "firebase/firestore";
import "./Login.css";
import logoAzienda from "../assets/Logoaziende.png";

export default function Login({ onLogin }) {
  const [error, setError] = useState("");
  const [isRegistering, setIsRegistering] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [savedEmail, setSavedEmail] = useState("");
  
  // Form data state
  const [formData, setFormData] = useState({
    nome: '',
    cognome: '',
    email: '',
    confirmEmail: '',
    password: '',
    confirmPassword: ''
  });
  
  // Validazione form
  const [formErrors, setFormErrors] = useState({});

  // Recupera l'email salvata al caricamento del componente
  useEffect(() => {
    const savedEmail = localStorage.getItem("rememberedEmail");
    if (savedEmail) {
      setSavedEmail(savedEmail);
      setRememberMe(true);
      setFormData(prev => ({ ...prev, email: savedEmail }));
    }
  }, []);

  // Reset del form quando cambia la modalità
  useEffect(() => {
    if (!isRegistering) {
      // Quando si passa a login, mantieni solo email
      const email = formData.email || savedEmail || '';
      setFormData({
        nome: '',
        cognome: '',
        email: email,
        confirmEmail: '',
        password: '',
        confirmPassword: ''
      });
    } else {
      // Quando si passa a registrazione, reset completo
      setFormData({
        nome: '',
        cognome: '',
        email: '',
        confirmEmail: '',
        password: '',
        confirmPassword: ''
      });
    }
    setFormErrors({});
    setError("");
  }, [isRegistering]);

  // Gestione cambiamenti input
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Rimuovi l'errore del campo quando l'utente inizia a digitare
    if (formErrors[name]) {
      setFormErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  // Funzione per creare o aggiornare il documento utente
  const createUserDocument = async (uid, userData) => {
    try {
      const userDocRef = doc(db, "users", uid);
      await setDoc(userDocRef, {
        ...userData,
        uid: uid,
        // Aggiungi lo stato account attivo di default per i nuovi utenti
        accountStatus: {
          active: true,
          lastUpdated: new Date(),
          reason: ""
        },
        // QR status attivo di default
        qrStatus: {
          active: true,
          lastUpdated: new Date()
        }
      }, { merge: true });
      
      console.log(`Documento utente creato/aggiornato per UID: ${uid}`);
    } catch (error) {
      console.error("Errore nella creazione del documento utente:", error);
      throw error;
    }
  };

  /**
   * Verifica se l'account dell'utente è attivo
   * @param {string} userId - L'ID dell'utente da verificare
   * @returns {Promise<boolean>} - True se l'account è attivo, false altrimenti
   */
  const checkAccountStatus = async (userId) => {
    try {
      const userDoc = await getDoc(doc(db, 'users', userId));
      
      if (!userDoc.exists()) {
        // Se il documento non esiste, l'account non è configurato correttamente
        console.warn('Documento utente non trovato per:', userId);
        return true; // Permetti l'accesso per non bloccare utenti esistenti
      }
      
      const userData = userDoc.data();
      
      // Verifica lo stato dell'account
      if (userData.accountStatus && userData.accountStatus.active === false) {
        // Account disabilitato
        const reason = userData.accountStatus.reason || "Account disabilitato";
        const disabledDate = userData.accountStatus.disabledDate;
        
        let errorMessage = "Il tuo account è stato disabilitato.";
        
        if (reason.includes("non più in forza")) {
          errorMessage = "Il tuo account è stato disabilitato perché non fai più parte dell'organizzazione.";
        }
        
        if (disabledDate) {
          const date = disabledDate.toDate ? disabledDate.toDate() : new Date(disabledDate);
          errorMessage += ` (Dal ${date.toLocaleDateString('it-IT')})`;
        }
        
        errorMessage += "\n\nPer maggiori informazioni, contatta l'amministratore.";
        
        // Effettua il logout immediato
        await signOut(auth);
        
        throw new Error(errorMessage);
      }
      
      // Account attivo
      return true;
    } catch (error) {
      console.error('Errore verifica stato account:', error);
      throw error;
    }
  };

  // Aggiorna l'ultimo accesso dell'utente
  const updateLastLogin = async (userId) => {
    try {
      const userRef = doc(db, "users", userId);
      await updateDoc(userRef, {
        lastLogin: new Date()
      });
    } catch (error) {
      console.error("Errore nell'aggiornamento dell'ultimo accesso:", error);
      // Non bloccare il login se l'aggiornamento fallisce
    }
  };

  // Validazione form
  const validateForm = () => {
    const errors = {};
    
    if (isRegistering) {
      // Validazione per la registrazione
      if (!formData.nome || formData.nome.trim() === '') {
        errors.nome = "Il nome è obbligatorio";
      }
      
      if (!formData.cognome || formData.cognome.trim() === '') {
        errors.cognome = "Il cognome è obbligatorio";
      }
      
      if (!formData.email || formData.email.trim() === '') {
        errors.email = "L'email è obbligatoria";
      } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
        errors.email = "Inserisci un'email valida";
      }
      
      if (!formData.confirmEmail || formData.confirmEmail.trim() === '') {
        errors.confirmEmail = "Conferma la tua email";
      } else if (formData.email !== formData.confirmEmail) {
        errors.confirmEmail = "Le email non corrispondono";
      }
      
      if (!formData.password) {
        errors.password = "La password è obbligatoria";
      } else if (formData.password.length < 6) {
        errors.password = "La password deve contenere almeno 6 caratteri";
      }
      
      if (!formData.confirmPassword) {
        errors.confirmPassword = "Conferma la tua password";
      } else if (formData.password !== formData.confirmPassword) {
        errors.confirmPassword = "Le password non corrispondono";
      }
    } else {
      // Validazione per il login
      if (!formData.email || formData.email.trim() === '') {
        errors.email = "L'email è obbligatoria";
      }
      
      if (!formData.password) {
        errors.password = "La password è obbligatoria";
      }
    }
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    
    // Validazione form
    if (!validateForm()) {
      return;
    }
    
    setIsLoading(true);

    // Gestisce il salvataggio dell'email
    if (rememberMe) {
      localStorage.setItem("rememberedEmail", formData.email);
    } else {
      localStorage.removeItem("rememberedEmail");
    }

    try {
      if (isRegistering) {
        // REGISTRAZIONE UTENTE
        console.log(`Avvio processo di registrazione per ${formData.email}`);
        
        // 1. Crea l'utente in Firebase Authentication
        const userCredential = await createUserWithEmailAndPassword(auth, formData.email, formData.password);
        const user = userCredential.user;
        
        console.log(`Utente creato in Authentication con UID: ${user.uid}`);
        
        // 2. Crea il documento utente in Firestore con account attivo
        const userData = {
          nome: formData.nome,
          cognome: formData.cognome,
          email: formData.email,
          role: "user",
          createdAt: new Date(),
          lastLogin: new Date()
        };
        
        // 3. Chiamata alla funzione di creazione documento
        await createUserDocument(user.uid, userData);
        
        // 4. Completa il processo di login
        onLogin(user);
      } else {
        // LOGIN UTENTE ESISTENTE
        console.log(`Avvio processo di login per ${formData.email}`);
        
        // 1. Autenticazione con Firebase
        const userCredential = await signInWithEmailAndPassword(auth, formData.email, formData.password);
        const user = userCredential.user;
        
        console.log(`Login riuscito per UID: ${user.uid}`);
        
        // 2. IMPORTANTE: Verifica se l'account è attivo
        try {
          await checkAccountStatus(user.uid);
        } catch (statusError) {
          // Se l'account è disabilitato, mostra l'errore e interrompi
          setError(statusError.message);
          return;
        }
        
        // 3. Verifica se esiste il documento utente
        const userDocRef = doc(db, "users", user.uid);
        const userDoc = await getDoc(userDocRef);
        
        // 4. Se il documento non esiste, crealo
        if (!userDoc.exists()) {
          console.log(`Documento utente non trovato, creazione...`);
          
          const userData = {
            email: formData.email,
            role: "user",
            createdAt: new Date(),
            lastLogin: new Date()
          };
          
          await createUserDocument(user.uid, userData);
        } else {
          // 5. Aggiorna l'ultimo accesso
          await updateLastLogin(user.uid);
        }
        
        // 6. Completa il processo di login
        onLogin(user);
      }
    } catch (err) {
      console.error("Errore durante l'autenticazione:", err);
      
      // Se l'errore è già personalizzato (account disabilitato), non sovrascriverlo
      if (err.message && err.message.includes("disabilitato")) {
        // L'errore è già stato impostato nel checkAccountStatus
        return;
      }
      
      let errorMessage = "Si è verificato un errore. Riprova.";
      if (err.code === "auth/user-not-found" || err.code === "auth/wrong-password") {
        errorMessage = "Credenziali non valide. Verifica email e password.";
      } else if (err.code === "auth/invalid-credential") {
        errorMessage = "Credenziali non valide. Verifica email e password.";
      } else if (err.code === "auth/email-already-in-use") {
        errorMessage = "Questa email è già registrata. Prova ad accedere.";
      } else if (err.code === "auth/weak-password") {
        errorMessage = "La password deve contenere almeno 6 caratteri.";
      } else if (err.code === "auth/network-request-failed") {
        errorMessage = "Errore di connessione. Verifica la tua connessione internet.";
      } else if (err.code === "auth/too-many-requests") {
        errorMessage = "Troppi tentativi falliti. Riprova tra qualche minuto.";
      }
      
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  // Toggle per cambiare la modalità di registrazione/login
  const toggleMode = () => {
    setIsRegistering(!isRegistering);
  };

  // Toggle per l'opzione "ricordami"
  const toggleRememberMe = () => {
    setRememberMe(!rememberMe);
  };

  return (
    <div className="login-page">
      <div className="login-container">
        <div className="login-card">
          <div className="login-branding">
            <div className="login-logo">
              <img src={logoAzienda} alt="Iacuzzo Construction Group Logo" />
            </div>
            <h1>App - Gruppo Iacuzzo</h1>
          </div>
          
          <div className="login-header">
            <h2>{isRegistering ? "Crea un nuovo account" : "Benvenuto"}</h2>
            <p>
              {isRegistering 
                ? "Registrati per accedere al sistema di gestione"
                : "Accedi al tuo account per continuare"}
            </p>
          </div>

          <form onSubmit={handleSubmit}>
            {/* Campi nome e cognome solo per registrazione */}
            {isRegistering && (
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="nome">Nome</label>
                  <div className="input-icon-wrapper">
                    <input
                      type="text"
                      id="nome"
                      name="nome"
                      value={formData.nome}
                      onChange={handleInputChange}
                      placeholder="Il tuo nome"
                      className={formErrors.nome ? 'error' : ''}
                    />
                    <span className="input-icon">
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                        <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
                        <circle cx="12" cy="7" r="4"></circle>
                      </svg>
                    </span>
                  </div>
                  {formErrors.nome && (
                    <span className="error-message-field">{formErrors.nome}</span>
                  )}
                </div>

                <div className="form-group">
                  <label htmlFor="cognome">Cognome</label>
                  <div className="input-icon-wrapper">
                    <input
                      type="text"
                      id="cognome"
                      name="cognome"
                      value={formData.cognome}
                      onChange={handleInputChange}
                      placeholder="Il tuo cognome"
                      className={formErrors.cognome ? 'error' : ''}
                    />
                    <span className="input-icon">
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                        <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
                        <circle cx="12" cy="7" r="4"></circle>
                      </svg>
                    </span>
                  </div>
                  {formErrors.cognome && (
                    <span className="error-message-field">{formErrors.cognome}</span>
                  )}
                </div>
              </div>
            )}

            {/* Campo email */}
            <div className="form-group">
              <label htmlFor="email">Email</label>
              <div className="input-icon-wrapper">
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  placeholder="nome@azienda.com"
                  className={formErrors.email ? 'error' : ''}
                />
                <span className="input-icon">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                    <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"></path>
                    <polyline points="22,6 12,13 2,6"></polyline>
                  </svg>
                </span>
              </div>
              {formErrors.email && (
                <span className="error-message-field">{formErrors.email}</span>
              )}
            </div>

            {/* Conferma email solo per registrazione */}
            {isRegistering && (
              <div className="form-group">
                <label htmlFor="confirmEmail">Conferma Email</label>
                <div className="input-icon-wrapper">
                  <input
                    type="email"
                    id="confirmEmail"
                    name="confirmEmail"
                    value={formData.confirmEmail}
                    onChange={handleInputChange}
                    placeholder="Conferma la tua email"
                    className={formErrors.confirmEmail ? 'error' : ''}
                  />
                  <span className="input-icon">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                      <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"></path>
                      <polyline points="22,6 12,13 2,6"></polyline>
                    </svg>
                  </span>
                </div>
                {formErrors.confirmEmail && (
                  <span className="error-message-field">{formErrors.confirmEmail}</span>
                )}
              </div>
            )}

            {/* Campo password */}
            <div className="form-group">
              <label htmlFor="password">Password</label>
              <div className="input-icon-wrapper">
                <input
                  type="password"
                  id="password"
                  name="password"
                  value={formData.password}
                  onChange={handleInputChange}
                  placeholder={isRegistering ? "Minimo 6 caratteri" : "La tua password"}
                  className={formErrors.password ? 'error' : ''}
                />
                <span className="input-icon">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                    <rect x="3" y="11" width="18" height="11" rx="2" ry="2"></rect>
                    <path d="M7 11V7a5 5 0 0 1 10 0v4"></path>
                  </svg>
                </span>
              </div>
              {formErrors.password && (
                <span className="error-message-field">{formErrors.password}</span>
              )}
            </div>

            {/* Conferma password solo per registrazione */}
            {isRegistering && (
              <div className="form-group">
                <label htmlFor="confirmPassword">Conferma Password</label>
                <div className="input-icon-wrapper">
                  <input
                    type="password"
                    id="confirmPassword"
                    name="confirmPassword"
                    value={formData.confirmPassword}
                    onChange={handleInputChange}
                    placeholder="Conferma la tua password"
                    className={formErrors.confirmPassword ? 'error' : ''}
                  />
                  <span className="input-icon">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                      <rect x="3" y="11" width="18" height="11" rx="2" ry="2"></rect>
                      <path d="M7 11V7a5 5 0 0 1 10 0v4"></path>
                    </svg>
                  </span>
                </div>
                {formErrors.confirmPassword && (
                  <span className="error-message-field">{formErrors.confirmPassword}</span>
                )}
              </div>
            )}

            {/* Remember me checkbox solo per login */}
            {!isRegistering && (
              <div className="remember-me">
                <label className="checkbox-container">
                  <input
                    type="checkbox"
                    checked={rememberMe}
                    onChange={toggleRememberMe}
                  />
                  <span className="checkmark"></span>
                  <span className="checkbox-label">Ricorda la mia email</span>
                </label>
              </div>
            )}

            {/* Messaggio di errore generale con stile migliorato per account disabilitato */}
            {error && (
              <div className={`error-message ${error.includes('disabilitato') ? 'error-account-disabled' : ''}`}>
                {error.split('\n').map((line, index) => (
                  <div key={index}>{line}</div>
                ))}
              </div>
            )}

            {/* Submit button */}
            <button type="submit" className="submit-button" disabled={isLoading}>
              {isLoading ? (
                <div className="loader-container">
                  <div className="loader"></div>
                  <span>{isRegistering ? "Registrazione..." : "Accesso..."}</span>
                </div>
              ) : (
                <span>{isRegistering ? "Registrati" : "Accedi"}</span>
              )}
            </button>
          </form>

          {/* Toggle form */}
          <div className="toggle-form">
            <p>
              {isRegistering 
                ? "Hai già un account?" 
                : "Non hai ancora un account?"}
            </p>
            <button 
              type="button" 
              className="toggle-button" 
              onClick={toggleMode}
              disabled={isLoading}
            >
              {isRegistering ? "Accedi" : "Registrati"}
            </button>
          </div>
        </div>
      </div>

      <div className="login-footer">
        <p>© 2024 Iacuzzo Construction Group. Tutti i diritti riservati.</p>
      </div>

      {/* Stili aggiuntivi per account disabilitato */}
      <style jsx>{`
        .error-account-disabled {
          background-color: #fff5f5;
          color: #dc3545;
          padding: 16px;
          border-radius: 10px;
          font-size: 14px;
          border-left: 4px solid #dc3545;
          border: 2px solid #dc3545;
          background: linear-gradient(135deg, #fff5f5 0%, #ffe0e0 100%);
        }

        .error-account-disabled::before {
          content: "⛔";
          font-size: 20px;
          margin-right: 10px;
        }

        .error-account-disabled div:first-child {
          font-weight: 600;
          margin-bottom: 8px;
        }

        .error-account-disabled div:last-child {
          margin-top: 10px;
          padding-top: 10px;
          border-top: 1px solid rgba(220, 53, 69, 0.3);
          font-style: italic;
        }
      `}</style>
    </div>
  );
}